import useMediaQuery from 'hooks/useMediaQuery';
import React, { useState } from 'react';
import { IoMdExit } from 'react-icons/io';
import { MdMenu } from 'react-icons/md';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 72;

const AdminLayout = ({ menu, children, title, root, isCollapsed, badges, ...props }) => {
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [collapsed, setCollapsed] = useState(isCollapsed || isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('user-info');
    localStorage.removeItem('user-jwt-tk');
    localStorage.removeItem('user-jwt-rftk');
    window.location.reload();
  };

  const navigate = useNavigate();
  const currentPath = window.location.pathname;

  const sidebarOpen = isMobile ? mobileOpen : true;
  const sidebarCollapsed = isMobile ? false : collapsed;
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const handleNavigate = (path) => {
    navigate(root ? `${root}/${path}` : path);
    if (isMobile) setMobileOpen(false);
  };

  return (
    <Wrapper>
      {/* Mobile hamburger */}
      {isMobile && !mobileOpen && (
        <MobileMenuBtn onClick={() => setMobileOpen(true)}>
          <MdMenu size={22} />
        </MobileMenuBtn>
      )}

      {/* Backdrop for mobile */}
      {isMobile && mobileOpen && (
        <Backdrop onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <Sidebar
        $width={sidebarWidth}
        $open={sidebarOpen}
        $mobile={isMobile}
      >
        {/* Header / Logo */}
        <SidebarHeader $collapsed={sidebarCollapsed}>
          {!sidebarCollapsed && (
            <LogoArea>{title}</LogoArea>
          )}
        </SidebarHeader>

        {/* Nav items */}
        <NavList>
          {menu?.map((item) => {
            if (item.children === undefined) {
              const isActive = item.path === currentPath;
              const badgeCount = badges?.[item.path] || 0;
              return (
                <NavItem
                  key={item.path}
                  $active={isActive}
                  $collapsed={sidebarCollapsed}
                  onClick={() => handleNavigate(item.path)}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <NavIcon $active={isActive}>
                    {item.icon}
                    {badgeCount > 0 && <NavBadgeDot />}
                  </NavIcon>
                  {!sidebarCollapsed && <NavLabel>{item.label}</NavLabel>}
                  {!sidebarCollapsed && badgeCount > 0 && <NavBadge>{badgeCount > 99 ? '99+' : badgeCount}</NavBadge>}
                  {isActive && <ActiveBar />}
                </NavItem>
              );
            } else {
              return (
                <NavGroup key={item.path}>
                  {!sidebarCollapsed && (
                    <NavGroupTitle>{item.label}</NavGroupTitle>
                  )}
                  {item.children.map((sub) => {
                    const isActive = sub.path === currentPath;
                    return (
                      <NavItem
                        key={sub.path}
                        $active={isActive}
                        $collapsed={sidebarCollapsed}
                        $sub
                        onClick={() => handleNavigate(sub.path)}
                        title={sidebarCollapsed ? sub.label : undefined}
                      >
                        <NavIcon $active={isActive}>{sub.icon}</NavIcon>
                        {!sidebarCollapsed && <NavLabel>{sub.label}</NavLabel>}
                        {isActive && <ActiveBar />}
                      </NavItem>
                    );
                  })}
                </NavGroup>
              );
            }
          })}
        </NavList>

        {/* Footer */}
        <SidebarFooter>
          {!isMobile && (
            <CollapseBtn onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Mở rộng' : 'Thu gọn'}>
              {collapsed ? <BiChevronRight size={20} /> : <BiChevronLeft size={20} />}
              {!sidebarCollapsed && <span>Thu gọn</span>}
            </CollapseBtn>
          )}
          <LogoutBtn
            onClick={props?.handleLogout || handleLogout}
            $collapsed={sidebarCollapsed}
            title={sidebarCollapsed ? 'Đăng xuất' : undefined}
          >
            <IoMdExit size={18} />
            {!sidebarCollapsed && <span>Đăng xuất</span>}
          </LogoutBtn>
        </SidebarFooter>
      </Sidebar>

      {/* Main content */}
      <Main $offset={isMobile ? 0 : sidebarWidth}>
        {children || <Outlet />}
      </Main>
    </Wrapper>
  );
};

export default AdminLayout;

/* ─── styled ─── */

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  background: #f5f6fa;
`;

const MobileMenuBtn = styled.button`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 1100;
  border: none;
  background: #fff;
  color: #334155;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f1f5f9; }

  /* Bootstrap modal có z-index 1055, react-bootstrap Modal gắn
     class .modal-open lên <body> khi mở. Ẩn hamburger trong lúc đó
     để nó không che nút × của modal trên mobile. */
  body.modal-open & { display: none; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(2px);

  body.modal-open & { display: none; }
`;

const Sidebar = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1300;
  body.modal-open & { z-index: 1040; }
  width: ${p => p.$width}px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e8ecf1;
  box-shadow: 2px 0 12px rgba(0,0,0,0.04);
  transition: width 0.25s cubic-bezier(.4,0,.2,1),
              transform 0.25s cubic-bezier(.4,0,.2,1);
  overflow: hidden;

  ${p => p.$mobile && `
    width: ${SIDEBAR_WIDTH}px;
    transform: translateX(${p.$open ? '0' : '-100%'});
    box-shadow: ${p.$open ? '4px 0 24px rgba(0,0,0,0.12)' : 'none'};
  `}
`;

const SidebarHeader = styled.div`
  /* Top 20px giữ thoáng cạnh trên; bottom 4px ngắn lại — logo tròn
     nhỏ (64px) không cần khoảng cách 12px như khi logo là chữ nhật.
     Visual gọn hơn, nav items lên cao hơn dễ thấy. */
  padding: ${p => p.$collapsed ? '16px 0' : '20px 20px 4px'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: ${p => p.$collapsed ? '20px' : 'auto'};
`;

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  img {
    max-height: 60px;
    object-fit: contain;
  }
`;

const NavList = styled.nav`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
`;

const NavItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${p => p.$collapsed ? '10px 0' : '10px 14px'};
  margin: 2px 0;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  transition: background 0.15s, color 0.15s;
  color: ${p => p.$active ? '#2563eb' : '#475569'};
  background: ${p => p.$active ? '#eff6ff' : 'transparent'};
  font-weight: ${p => p.$active ? 600 : 400};

  &:hover {
    background: ${p => p.$active ? '#eff6ff' : '#f8fafc'};
    color: ${p => p.$active ? '#2563eb' : '#1e293b'};
  }

  ${p => p.$sub && !p.$collapsed && `padding-left: 28px;`}
`;

const NavIcon = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: ${p => p.$active ? '#2563eb' : '#64748b'};
  transition: color 0.15s;

  svg { width: 18px; height: 18px; }
`;

const NavLabel = styled.span`
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const NavBadge = styled.span`
  margin-left: auto;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  min-width: 18px;
  text-align: center;
  line-height: 15px;
  flex-shrink: 0;
`;

const NavBadgeDot = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
`;

const ActiveBar = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: 0 3px 3px 0;
  background: #2563eb;
`;

const NavGroup = styled.div`
  margin-top: 8px;
`;

const NavGroupTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  padding: 8px 14px 4px;
`;

const SidebarFooter = styled.div`
  flex-shrink: 0;
  padding: 8px;
  border-top: 1px solid #f1f5f9;
`;

const CollapseBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border: none;
  background: transparent;
  color: #64748b;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s, color 0.15s;

  &:hover { background: #f8fafc; color: #334155; }

  span { white-space: nowrap; }
`;

const LogoutBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  gap: 10px;
  padding: 9px 14px;
  border: none;
  background: transparent;
  color: #ef4444;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s;

  &:hover { background: #fef2f2; }

  span { white-space: nowrap; }
`;

const Main = styled.div`
  margin-left: ${p => p.$offset}px;
  width: calc(100% - ${p => p.$offset}px);
  min-height: 100vh;
  overflow-x: hidden;
  transition: margin-left 0.25s cubic-bezier(.4,0,.2,1),
              width 0.25s cubic-bezier(.4,0,.2,1);
`;
